import type { MutableRefObject } from 'react';
import { useCallback, useMemo, useState } from 'react';
import {
  collectExpandableNodeIds,
  findMindmapNodeById,
  getVisibleSubtreeIds,
} from './layout-mindmap';
import {
  MINDMAP_FIT_TARGET,
  type PendingMindmapFitState,
} from './mindmap-fit-state';
import type { MindmapExpansionInput } from './mindmap-navigation';
import type { ParsedMindmap } from './mindmap-types';

interface ExpandedNodeState {
  rootId: string | null;
  nodeIds: Set<string>;
}

interface UseMindmapExpansionStateInput {
  parsed: ParsedMindmap | null;
  pendingFitRef: MutableRefObject<PendingMindmapFitState>;
}

interface UseMindmapExpansionStateResult {
  expandedNodeIds: Set<string>;
  handleCollapseAll: () => void;
  handleExpandAll: () => void;
  onToggleExpand: (nodeId: string) => void;
  updateNodeExpansion: (input: MindmapExpansionInput) => void;
}

export function useMindmapExpansionState(
  input: UseMindmapExpansionStateInput,
): UseMindmapExpansionStateResult {
  const { parsed, pendingFitRef } = input;
  const [expandedNodeState, setExpandedNodeState] = useState<ExpandedNodeState>(() => {
    return getInitialExpandedNodeState(parsed);
  });

  const expandedNodeIds = useMemo(() => {
    if (!parsed) {
      return new Set<string>();
    }

    if (expandedNodeState.rootId === parsed.root.id) {
      return expandedNodeState.nodeIds;
    }

    return new Set<string>([parsed.root.id]);
  }, [expandedNodeState, parsed]);

  const updateNodeExpansion = useCallback((expansionInput: MindmapExpansionInput): void => {
    if (!parsed) {
      return;
    }

    const targetNode = findMindmapNodeById({
      root: parsed.root,
      nodeId: expansionInput.nodeId,
    });

    if (!targetNode || targetNode.children.length === 0) {
      return;
    }

    setExpandedNodeState((previousExpandedNodeState) => {
      const update = getNextExpandedNodeUpdate({
        expansionInput,
        previousExpandedNodeState,
        root: parsed.root,
      });
      if (!update) {
        return previousExpandedNodeState;
      }

      pendingFitRef.current = update.pendingFitState;
      return update.expandedNodeState;
    });
  }, [parsed, pendingFitRef]);

  const onToggleExpand = useCallback((nodeId: string): void => {
    updateNodeExpansion({ nodeId });
  }, [updateNodeExpansion]);

  const handleExpandAll = useCallback((): void => {
    if (!parsed) {
      return;
    }

    setExpandedNodeState({
      rootId: parsed.root.id,
      nodeIds: collectExpandableNodeIds(parsed.root),
    });
    pendingFitRef.current = {
      target: MINDMAP_FIT_TARGET.All,
      nodeIds: [],
    };
  }, [parsed, pendingFitRef]);

  const handleCollapseAll = useCallback((): void => {
    if (!parsed) {
      return;
    }

    setExpandedNodeState({
      rootId: parsed.root.id,
      nodeIds: new Set<string>([parsed.root.id]),
    });
    pendingFitRef.current = {
      target: MINDMAP_FIT_TARGET.All,
      nodeIds: [],
    };
  }, [parsed, pendingFitRef]);

  return {
    expandedNodeIds,
    handleCollapseAll,
    handleExpandAll,
    onToggleExpand,
    updateNodeExpansion,
  };
}

function getInitialExpandedNodeState(parsed: ParsedMindmap | null): ExpandedNodeState {
  if (!parsed) {
    return {
      rootId: null,
      nodeIds: new Set<string>(),
    };
  }

  return {
    rootId: parsed.root.id,
    nodeIds: new Set<string>([parsed.root.id]),
  };
}

function getNextExpandedNodeUpdate(input: {
  expansionInput: MindmapExpansionInput;
  previousExpandedNodeState: ExpandedNodeState;
  root: ParsedMindmap['root'];
}): {
  expandedNodeState: ExpandedNodeState;
  pendingFitState: PendingMindmapFitState;
} | null {
  const baseExpandedNodeIds = getBaseExpandedNodeIds({
    previousExpandedNodeState: input.previousExpandedNodeState,
    rootId: input.root.id,
  });
  const isExpanded = baseExpandedNodeIds.has(input.expansionInput.nodeId);
  const shouldExpand = input.expansionInput.forceExpanded ?? !isExpanded;

  if (shouldExpand === isExpanded) {
    return null;
  }

  const nextExpandedNodeIds = getNextExpandedNodeIds({
    baseExpandedNodeIds,
    nodeId: input.expansionInput.nodeId,
    shouldExpand,
  });

  return {
    expandedNodeState: {
      rootId: input.root.id,
      nodeIds: nextExpandedNodeIds,
    },
    pendingFitState: getPendingFitStateForExpansion({
      expandedNodeIds: nextExpandedNodeIds,
      nodeId: input.expansionInput.nodeId,
      root: input.root,
      shouldExpand,
    }),
  };
}

function getBaseExpandedNodeIds(input: {
  previousExpandedNodeState: ExpandedNodeState;
  rootId: string;
}): Set<string> {
  if (input.previousExpandedNodeState.rootId === input.rootId) {
    return input.previousExpandedNodeState.nodeIds;
  }

  return new Set<string>([input.rootId]);
}

function getNextExpandedNodeIds(input: {
  baseExpandedNodeIds: Set<string>;
  nodeId: string;
  shouldExpand: boolean;
}): Set<string> {
  const nextExpandedNodeIds = new Set(input.baseExpandedNodeIds);
  if (input.shouldExpand) {
    nextExpandedNodeIds.add(input.nodeId);
  } else {
    nextExpandedNodeIds.delete(input.nodeId);
  }

  return nextExpandedNodeIds;
}

function getPendingFitStateForExpansion(input: {
  expandedNodeIds: Set<string>;
  nodeId: string;
  root: ParsedMindmap['root'];
  shouldExpand: boolean;
}): PendingMindmapFitState {
  if (!input.shouldExpand) {
    return {
      target: MINDMAP_FIT_TARGET.All,
      nodeIds: [],
    };
  }

  return {
    target: MINDMAP_FIT_TARGET.Subtree,
    nodeIds: Array.from(
      getVisibleSubtreeIds({
        root: input.root,
        nodeId: input.nodeId,
        expandedNodeIds: input.expandedNodeIds,
      }),
    ),
  };
}
